import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Wallet, CreditCard, ChevronDown, Check, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../contexts/AppContext';
import AddCardModal from '../modals/AddCardModal';

/**
 * Inline-only payment picker for in-call tips and gifts.
 * Wallet + saved cards on inline-capable gateways (stripe, ccbill, netbilling).
 * Excludes redirect flows (FlexForms, Stripe Checkout) — must not yank the user out of the call.
 *
 * The dropdown renders via portal so it can't be clipped by the parent modal's overflow-hidden.
 */
const INLINE_GATEWAYS = ['stripe', 'ccbill', 'netbilling'];

const getCardBrandIcon = (brand) => {
  const b = brand?.toLowerCase();
  if (b && ['visa', 'mastercard', 'amex', 'discover', 'jcb', 'diners', 'maestro'].includes(b)) {
    return (
      <img
        src={`/payicon/${b}.png`}
        alt={b}
        style={{ height: 18, width: 'auto', maxWidth: 32, objectFit: 'contain' }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  }
  return <CreditCard className="h-4 w-4 text-gray-400" />;
};

const InlinePaymentPicker = ({ value, onChange }) => {
  const { t } = useTranslation('livestream');
  const { savedCards, siteSettings, walletBalance, refreshPaymentMethods } = useApp();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const options = useMemo(() => {
    const list = [];
    if (siteSettings?.wallet_enabled) {
      list.push({
        key: 'wallet',
        label: t('videoCall.paymentPicker.wallet', { balance: Number(walletBalance).toFixed(2) }),
        icon: <Wallet className="w-4 h-4 text-emerald-500" />,
        gateway: 'wallet',
        paymentMethodId: null,
      });
    }
    (savedCards || [])
      .filter((c) => INLINE_GATEWAYS.includes(c.gateway))
      .forEach((c) => {
        const brand = c.card_brand ? c.card_brand[0].toUpperCase() + c.card_brand.slice(1) : t('videoCall.paymentPicker.card');
        list.push({
          key: `card_${c.id}`,
          label: `${brand} •••• ${c.card_last_four}`,
          icon: getCardBrandIcon(c.card_brand),
          gateway: c.gateway,
          paymentMethodId: c.id,
        });
      });
    // UI-only: keep "Add new card" entries at the bottom of the picker.
    list.sort((a, b) => {
      const aIsAdd = a.type === 'add_card';
      const bIsAdd = b.type === 'add_card';
      if (aIsAdd && !bIsAdd) return 1;
      if (bIsAdd && !aIsAdd) return -1;
      return 0;
    });
    return list;
  }, [savedCards, siteSettings, walletBalance, t]);

  // Default selection — wallet if enabled, else first card
  useEffect(() => {
    if (!value && options.length > 0) {
      onChange({ gateway: options[0].gateway, paymentMethodId: options[0].paymentMethodId });
    }
  }, [options, value, onChange]);

  const selected = options.find((o) => o.gateway === value?.gateway
    && (o.paymentMethodId ?? null) === (value?.paymentMethodId ?? null));

  // Compute dropdown position when opening, and keep it synced on scroll/resize
  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const dropdownWidth = r.width;
    const dropdownMaxHeight = 280;
    const spaceBelow = window.innerHeight - r.bottom;
    const placeAbove = spaceBelow < dropdownMaxHeight + 16 && r.top > dropdownMaxHeight + 16;
    setPos({
      left: r.left,
      top: placeAbove ? undefined : r.bottom + 4,
      bottom: placeAbove ? window.innerHeight - r.top + 4 : undefined,
      width: dropdownWidth,
      maxHeight: dropdownMaxHeight,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    const handleScroll = () => updatePos();
    const handleResize = () => updatePos();
    const handleClick = (e) => {
      if (dropdownRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClick);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open, updatePos]);

  const handleCardAdded = (cardData) => {
    setShowAddCard(false);
    refreshPaymentMethods().then(() => {
      if (cardData?.payment_method_id) {
        onChange({ gateway: 'ccbill', paymentMethodId: cardData.payment_method_id });
      }
    });
  };

  const triggerContent = (
    <>
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="w-6 flex items-center justify-center flex-shrink-0">
          {selected?.icon || <CreditCard className="w-4 h-4 text-gray-400" />}
        </span>
        <span className="text-sm text-gray-800 dark:text-gray-100 truncate">
          {selected?.label || t('videoCall.paymentPicker.selectPayment')}
        </span>
      </div>
      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
    </>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 flex items-center justify-between gap-2 hover:border-gray-300 dark:hover:border-neutral-600 transition-colors"
      >
        {triggerContent}
      </button>

      {open && pos && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[999] bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-xl overflow-y-auto"
          style={{
            left: pos.left,
            top: pos.top,
            bottom: pos.bottom,
            width: pos.width,
            maxHeight: pos.maxHeight,
          }}
        >
          {options.length === 0 && (
            <div className="px-3 py-3 text-xs text-amber-600 dark:text-amber-400">
              {t('videoCall.paymentPicker.noOptions')}
            </div>
          )}
          {options.map((o, i) => {
            const isSelected = selected?.key === o.key;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => { onChange({ gateway: o.gateway, paymentMethodId: o.paymentMethodId }); setOpen(false); }}
                className={`w-full h-11 px-3 flex items-center gap-2.5 text-left transition-colors
                  ${i === 0 ? 'rounded-t-lg' : ''}
                  ${isSelected ? '' : 'hover:bg-gray-50 dark:hover:bg-neutral-800'}`}
                style={isSelected ? { backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 12%, transparent)' } : {}}
              >
                <span className="w-6 flex items-center justify-center flex-shrink-0">{o.icon}</span>
                <span className="text-sm text-gray-800 dark:text-gray-100 truncate flex-1">{o.label}</span>
                {isSelected && <Check className="w-3.5 h-3.5" style={{ color: 'var(--color-primary-500)' }} />}
              </button>
            );
          })}

          {/* Add-new-card anchor */}
          <button
            type="button"
            onClick={() => { setOpen(false); setShowAddCard(true); }}
            className="w-full h-11 px-3 flex items-center gap-2.5 text-left border-t border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800 rounded-b-lg transition-colors"
          >
            <span className="w-6 flex items-center justify-center flex-shrink-0">
              <Plus className="w-4 h-4" style={{ color: 'var(--color-primary-500)' }} />
            </span>
            <span className="text-sm font-medium" style={{ color: 'var(--color-primary-600)' }}>
              {t('videoCall.paymentPicker.addNewCard')}
            </span>
          </button>
        </div>,
        document.body
      )}

      <AddCardModal
        isOpen={showAddCard}
        onClose={() => setShowAddCard(false)}
        onCardAdded={handleCardAdded}
      />
    </>
  );
};

export default InlinePaymentPicker;
